import Chart, {
  ScatterDataPoint,
  ChartDataset,
  ChartConfiguration,
  ScaleOptionsByType,
  ScaleTypeRegistry,
} from 'chart.js/auto'
import zoomPlugin from 'chartjs-plugin-zoom'
import { DeepPartial } from 'chart.js/types/utils'
import { sortBy } from 'lodash'

Chart.register(zoomPlugin)

export type ScaleOption = {
  [key: string]: DeepPartial<ScaleOptionsByType<keyof ScaleTypeRegistry>> & {
    scaleLabel?: {
      labelString: string
      labelColor: string
    }
  }
}

export default abstract class IWSChart {
  public chartjs?: Chart<'line' | 'bar'>
  public options?: ChartConfiguration
  abstract getRange(center: number): { start: number; end: number }

  constructor(public canvas: HTMLCanvasElement) {}

  get ctx() {
    return this.canvas.getContext('2d') as CanvasRenderingContext2D
  }

  get datasets() {
    return this.chartjs?.data.datasets || []
  }

  get min() {
    if (!this.datasets[0].data === undefined) {
      return
    }

    return this.datasets[0].data[0] as ScatterDataPoint
  }

  get max() {
    const data = this.datasets[0].data

    return data[data.length - 1] as ScatterDataPoint
  }

  _render(options: ChartConfiguration<'line'>) {
    if (!options) {
      return
    }

    this.chartjs = new Chart<'line'>(this.ctx, options)
  }

  update() {
    this.chartjs?.update()
  }

  destroy() {
    this.chartjs?.destroy()
  }

  getYAxisScalePosition(labelName: string) {
    const chart = this.chartjs

    if (!chart) {
      return {
        top: 0,
        left: 0,
      }
    }

    const top = chart.scales[labelName].top
    const left = chart.scales[labelName].left
    const right = chart.scales[labelName].right

    return {
      top: top - 20,
      left: (left + right) / 2 - 5,
    }
  }

  getCenter() {
    if (!this.chartjs) {
      return
    }

    const { min, max } = this.chartjs.scales['x'].getMinMax(true)

    return Math.floor((min + max) / 2)
  }

  // グラフ中心位置を指定して対象の表示領域にクロップ
  updateZoomScale(center: number) {
    const { start: min, end: max } = this.getRange(center)

    this.chartjs?.zoomScale('x', { min, max })
    this.chartjs?.update()
  }

  // 新規グラフデータ追加
  setData(data: ChartDataset<'line'>[]) {
    if (!this.chartjs) {
      return
    }

    // 新規データをmerge
    this.chartjs.data.datasets = [...this.datasets].map(original => {
      const target = data.find(d => d.label === original.label)

      if (!target) {
        return original
      }

      // 計測時間でソート
      original.data = sortBy([...original.data, ...target.data], 'x')

      return original
    })

    this.chartjs.update()
  }
}
